import { useCallback, useEffect, useState } from "react";

/**
 * Whether a newer version of the app is sitting on the device, waiting.
 *
 * An installed app can stay open for days. Nothing forces a reload, so a
 * deployed fix reaches nobody until each user happens to restart the app - and
 * in a Trusted Web Activity there is no address bar to reload from. This hook
 * notices the new version and gives the UI a way to switch to it.
 *
 * It does not register the service worker; `registerSW.js` does that at load.
 * Registering a second time here would race with it for no gain.
 *
 * The waiting state is what "a new version is available" actually means: the
 * new worker has downloaded and installed, and is holding back until the old
 * one is released. That is also why service-worker.ts must NOT call
 * skipWaiting() on install - a worker that takes over immediately never waits,
 * so there is nothing to announce and the page keeps running the old code
 * anyway until something reloads it.
 */

/** How often to ask the server whether a newer worker exists. */
const CHECK_INTERVAL_MS = 60_000;

export interface AppUpdate {
  /** A new version has installed and is waiting to take over. */
  updateAvailable: boolean;
  /** Switch to it and reload. Safe to call when nothing is waiting. */
  applyUpdate: () => void;
}

export function useAppUpdate(): AppUpdate {
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | undefined;
    const cleanups: Array<() => void> = [];

    const offer = (worker: ServiceWorker | null) => {
      if (cancelled || !worker) return;

      // No controller means this page is not being served by a service worker
      // yet - the very first visit. The worker installing then is the first
      // one, not a new version, and announcing it would be a lie.
      const controller = navigator.serviceWorker.controller;
      if (!controller) return;

      // A different script is not a newer version of this one. Something that
      // registers a second worker at the same scope leaves it stuck in
      // "waiting" forever, and it has no reason to understand SKIP_WAITING -
      // so the prompt would appear and then do nothing when tapped. That
      // shipped once; the cause was fixed, and this keeps the symptom from
      // coming back silently.
      if (worker.scriptURL !== controller.scriptURL) return;

      setWaiting(worker);
    };

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (!registration || cancelled) return;

        // Already waiting when this mounted - the usual case when the app was
        // closed and reopened after a deploy.
        offer(registration.waiting);

        const onUpdateFound = () => {
          const installing = registration.installing;
          if (!installing) return;

          const onStateChange = () => {
            if (installing.state === "installed") {
              offer(installing);
            }
          };
          installing.addEventListener("statechange", onStateChange);
          cleanups.push(() =>
            installing.removeEventListener("statechange", onStateChange),
          );
        };

        registration.addEventListener("updatefound", onUpdateFound);
        cleanups.push(() =>
          registration.removeEventListener("updatefound", onUpdateFound),
        );

        // Ask, rather than wait to be told. A page left open overnight is told
        // nothing; the browser only re-fetches the worker on navigation.
        const check = () => void registration.update().catch(() => {});
        interval = setInterval(check, CHECK_INTERVAL_MS);

        // Coming back to the app is the moment a stale version is most likely
        // and the interruption least costly.
        const onVisible = () => {
          if (document.visibilityState === "visible") check();
        };
        document.addEventListener("visibilitychange", onVisible);
        cleanups.push(() =>
          document.removeEventListener("visibilitychange", onVisible),
        );

        check();
      })
      .catch(() => {
        // A browser that refuses to hand over the registration simply means no
        // update prompt. Nothing here is worth breaking the app over.
      });

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      cleanups.forEach((fn) => fn());
    };
  }, []);

  /**
   * Take the new version while nobody is looking.
   *
   * The prompt alone was not enough: a client keeps running the old code until
   * someone taps it, and on 2026-09-19 that meant a browser still asking for a
   * URL that had been wrong for months, hours after the fix was live - with the
   * banner sitting there unnoticed.
   *
   * Hidden is the safe moment, and the reason the prompt exists in the first
   * place does not apply to it: nothing is being typed, read or saved in a
   * backgrounded tab, so swapping the code under it costs nothing. The banner
   * stays for anyone who wants it sooner.
   */
  useEffect(() => {
    if (!waiting) return;

    const applyWhenHidden = () => {
      if (document.visibilityState !== "hidden") return;
      // No reload here: the page is not in front of anyone, and it will be
      // served by the new worker the next time it is opened. Reloading a
      // hidden tab would discard whatever state it holds for no benefit.
      waiting.postMessage({ type: "SKIP_WAITING" });
    };

    document.addEventListener("visibilitychange", applyWhenHidden);
    return () =>
      document.removeEventListener("visibilitychange", applyWhenHidden);
  }, [waiting]);

  const applyUpdate = useCallback(() => {
    if (!waiting) return;

    // Reload once the new worker has actually taken over, not before: reloading
    // first would just re-serve the old version from the old worker.
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => window.location.reload(),
      { once: true },
    );
    waiting.postMessage({ type: "SKIP_WAITING" });
  }, [waiting]);

  return { updateAvailable: waiting !== null, applyUpdate };
}
